import {
  Button,
  Card,
  CardHeader,
  Divider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import * as Sentry from "@sentry/browser";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import {
  allDestinationsData,
  saasDestinations,
  SaaSDestination,
} from "../destinations/destinations";
import { DestinationKey, SaaSDestinationSlug } from "../destinations/types";
import useEnvironment from "../hooks/use-environment";
import Navigation from "../Navigation";
import { LoadedDestinationInfo } from "../types/global";
import {
  SortableTableColumn,
  TableRowForTransformationSet,
} from "./components/TransformsTable";
import { loadTransforms, organizeTransformations } from "./transformations";
import { TransformDefinitionSet } from "./transformationDefinition";
import {
  TransformationPageFilter,
  TransformationPageSort,
  TransformationPageSortColumns,
} from "./types";
import useArrayOfDestinations, {
  useArrayOfDestinationsBySlug,
} from "../hooks/useArrayOfDestinations";

const TransformationPage = () => {
  const { destination, fpEventId, transformationSetId } = useParams<{
    destination?: SaaSDestinationSlug;
    fpEventId?: string;
    transformationSetId?: string;
  }>();
  const history = useHistory();
  const alert = useAlert();
  const currentEnvironment = useEnvironment();
  const [loaded, setLoaded] = useState(false);
  const [showAllTransforms, setShowAllTransforms] = useState(true);
  const [allTransformationSets, setAllTransformationSets] = useState<
    TransformDefinitionSet[]
  >([]);
  const [
    currentFilter,
    setCurrentFilter,
  ] = useState<TransformationPageFilter | null>(null);
  const [currentSort, setCurrentSort] = useState<TransformationPageSort>({
    column: "createdAt",
    direction: "desc",
  });
  const [availableDestinations, setAvailableDestinations] = useState<
    Record<DestinationKey, string>
  >({} as Record<DestinationKey, string>);
  const [
    availableServerDestinations,
    setAvailableServerDestinations,
  ] = useState<Record<DestinationKey, string>>(
    {} as Record<DestinationKey, string>
  );
  const [openTransformGroup, setOpenTransformGroup] = useState<string>(
    transformationSetId || ""
  );

  const [transformations, setTransformations] = useState<
    TransformDefinitionSet[]
  >([]);
  const destinationData = useArrayOfDestinations();

  const reorganizeTransformations = () => {
    if (allTransformationSets) {
      setTransformations(
        organizeTransformations(allTransformationSets, {
          filter: currentFilter,
          sort: currentSort,
          includeAll: showAllTransforms,
        })
      );
    } else {
      setTransformations([]);
    }
  };

  const reloadAllTransforms = () =>
    loadTransforms(setAllTransformationSets, setLoaded, alert);
  const reloadAvailableDestinations = () => {
    if (destinationData) {
      const availableDestinationKeyToID: Partial<Record<
        DestinationKey,
        string
      >> = {};

      const availableServerDestinationKeyToID: Partial<Record<
        DestinationKey,
        string
      >> = {};
      _.forEach(destinationData, (destinationInfo: LoadedDestinationInfo) => {
        availableDestinationKeyToID[destinationInfo.name] = destinationInfo.id;
        if (destinationInfo.connection_mode === "server") {
          availableServerDestinationKeyToID[destinationInfo.name] =
            destinationInfo.id;
        }
      });
      setAvailableDestinations(
        availableDestinationKeyToID as Record<DestinationKey, string>
      );
      setAvailableServerDestinations(
        availableServerDestinationKeyToID as Record<DestinationKey, string>
      );

      // const eventDefinitionIDToName: Record<string, string> = {};
      // _.forEach(response.data.event_defs, (eventDef) => {
      //     eventDefinitionIDToName[eventDef.id] = eventDef.name;
      // });
    }
  };

  useEffect(() => {
    reloadAvailableDestinations();
  }, [destinationData]);

  // Whenever allTransformations, currentFilter, or currentSort changes, call reorganizeTransformations;
  useEffect(reorganizeTransformations, [
    allTransformationSets,
    currentSort,
    currentFilter,
    showAllTransforms,
  ]);

  useEffect(() => {
    reloadAllTransforms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEnvironment.ID]);

  useEffect(() => {
    if (destination) {
      setCurrentFilter({
        type: "destinationName",
        value: allDestinationsData[destination].key,
      });
    } else if (fpEventId) {
      setCurrentFilter({
        type: "eventDefId",
        value: fpEventId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, fpEventId]);

  const setSort = (column: TransformationPageSortColumns) => {
    if (currentSort.column === column) {
      setCurrentSort({
        column,
        direction: currentSort.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setCurrentSort({
        column,
        direction: "asc",
      });
    }
  };

  const createNewTransformation = () => {
    history.push(`/transformations/new`);
  };

  const handleOpenTransformSetForEdit = (id: string) => {
    setOpenTransformGroup(id);
    if (id) {
      history.replace(`/transformations/edit/${id}`);
    } else {
      history.replace("/transformations");
    }
  };

  const saasDestination =
    (destination && saasDestinations[destination]) || null;
  const allDestinationsOfType =
    useArrayOfDestinationsBySlug(destination ?? "") || [];
  const hasMultipleDestinations = useMemo(() => {
    return (
      saasDestination instanceof SaaSDestination &&
      saasDestination?.supportsMultiConfig &&
      allDestinationsOfType.length > 1
    );
  }, [saasDestination, allDestinationsOfType]);

  return (
    <>
      <Card className="overflow-x-visible my-4" style={{ overflow: "visible" }}>
        <CardHeader
          title={
            <>
              <div className="flex items-center">
                <div className="pr-4">
                  {saasDestination?.name} Transformations
                </div>

                {destination && (
                  <span>
                    <Switch
                      checked={showAllTransforms}
                      onChange={(event) =>
                        setShowAllTransforms(event.target.checked)
                      }
                    />
                    All applicable transformations
                  </span>
                )}

                <div className="flex-grow" />

                {transformations.length !== 0 && (
                  <Button
                    className="mx-4"
                    color="primary"
                    onClick={createNewTransformation}
                    variant="contained"
                  >
                    <Add /> Create New
                  </Button>
                )}
              </div>
              <div className="flex items-center">
                {hasMultipleDestinations && (
                  <small>
                    There are {allDestinationsOfType.length}{" "}
                    {saasDestination?.name} destinations configured. These
                    transformations will apply to all of them.
                  </small>
                )}
              </div>
            </>
          }
        />
        <Divider />

        {loaded && (
          <Table>
            <TableHead>
              <TableRow>
                <SortableTableColumn
                  currentSort={currentSort}
                  column="intention"
                  handleClick={setSort}
                  text="Transformation Type"
                />

                <TableCell>Description</TableCell>

                {currentFilter && currentFilter.type === "eventDefId" ? (
                  <TableCell>Event</TableCell>
                ) : (
                  <SortableTableColumn
                    currentSort={currentSort}
                    column="eventDefName"
                    handleClick={setSort}
                    text="Event"
                  />
                )}
                {currentFilter && currentFilter.type === "destinationName" ? (
                  <TableCell>Destination</TableCell>
                ) : (
                  <SortableTableColumn
                    currentSort={currentSort}
                    column="destinationName"
                    handleClick={setSort}
                    text="Destination"
                  />
                )}
                <SortableTableColumn
                  currentSort={currentSort}
                  column="createdAt"
                  handleClick={setSort}
                  text="Created"
                />
                <TableCell className="text-center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transformations.map((transformationSet) => (
                <TableRowForTransformationSet
                  availableDestinations={availableDestinations}
                  availableServerDestinations={availableServerDestinations}
                  key={transformationSet.id}
                  reloadAllTransforms={reloadAllTransforms}
                  isOpen={transformationSet.id === openTransformGroup}
                  onOpen={handleOpenTransformSetForEdit}
                  transformationSet={transformationSet}
                />
              ))}
            </TableBody>
          </Table>
        )}

        {loaded && transformations.length === 0 && (
          <div className="w-full p-10 flex justify-center items-center flex-col">
            <div className="my-8 text-xl">
              You don't have any transformations yet!
            </div>

            <Button
              className="my-8"
              color="primary"
              onClick={createNewTransformation}
              variant="contained"
            >
              <Add /> New Transformation
            </Button>

            <div className="my-8 text-brand-navy">
              Learn more about transformations{" "}
              <a
                target="_blank"
                className="text-brand-red underline"
                href="https://docs.freshpaint.io/data-management/transformations"
              >
                in our docs
              </a>
              .
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

function TransformationPageWithNavigation() {
  return (
    <Navigation>
      <TransformationPage />
    </Navigation>
  );
}

export default TransformationPageWithNavigation;
